import {
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  Skeleton,
  Grid,
  Chip,
  Avatar,
  Fade,
} from '@mui/material';
import {
  LocalMallTwoTone, // Represents the "Goods" coming back
  TrendingUpRounded,
  TrendingDownRounded,
  TuneRounded,
  SpeedRounded,
  AccountTreeRounded,
  EditRounded,
} from '@mui/icons-material';
import { BuybackPriceDialog } from './Price/BuybackPriceDialog';
import { getprofitPercentage } from '../pages/buyback/Buyback';
import { useDialogContext } from './dialog-context';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import { useEffect, useState } from 'react';
import { services } from '../pages/Sell/services';

const CurrentBuybackPrice = ({
  setBullionsConfig,
  bullionsConfig,
  trig,
  trigger,
  pricePerGram,
  profitPercentageText,
  bullionTypes,
  manualPriceLessThanThreshhold,
  manualPriceMoreThanThreshhold,
  isPriceFirstLoading,
}) => {
  const { openDialog, closeDialog } = useDialogContext();
  const theme = useTheme();
  const [autoPriceSource, setAutoPriceSource] = useState('ESTJT');
  const [priceSources, setPriceSources] = useState([]);

  const firstCategory = bullionTypes.slice(0, 7);
  const secondCategory = bullionTypes.slice(7);

  // --- MODERN BUYBACK THEME (ORANGE / AMBER) ---
  // "Exchange Sell" concept without the "Error" look.
  // Orange is warm and active, sitting opposite to Green, perfect for "Buyback".
  const buybackTheme = {
    primary: '#ea580c', // Orange 600 (Burnt Orange)
    dark: '#9a3412', // Orange 900 (Brownish)
    light: '#ffedd5', // Orange 100
    bg: '#ffffff',
    hover: '#fff7ed', // Orange 50
    border: '#fed7aa', // Orange 200
    shadow:
      '0 10px 15px -3px rgba(234, 88, 12, 0.1), 0 4px 6px -2px rgba(234, 88, 12, 0.05)',
    headerGradient: 'linear-gradient(135deg, #ea580c 0%, #c2410c 100%)',
    textSecondary: '#64748b',
  };
  useEffect(() => {
    services
      .getPriceSource()
      .then(res => {
        if (res?.nameEn) {
          // setPriceSource(res.source);
          setAutoPriceSource(res.nameEn);
        }
      })
      .catch(err => {
        console.error('Error loading price source:', err);
      });
  }, [trigger]);

  useEffect(() => {
    services.getAvailablePriceSources().then(sources => {
      setPriceSources(sources);
    });
  }, [trigger]);

  // --- STRATEGY BADGE RENDERER ---
  const renderStrategyChip = strategy => {
    let label = strategy;
    let color = 'default';
    let icon = <TuneRounded sx={{ fontSize: 16 }} />;

    if (strategy === 'MANUAL') {
      label = 'دستی';
      color = 'default';
      icon = <EditRounded sx={{ fontSize: 16 }} />;
    } else if (strategy === 'AUTOMATIC_COMPETITOR') {
      label = 'رقیب';
      color = 'secondary';
      icon = <SpeedRounded sx={{ fontSize: 16 }} />;
    } else if (strategy === 'AUTOMATIC_ESTJT') {
      let source = 'مرجع قیمتی';

      source = priceSources.find(s => s.nameEn === autoPriceSource)?.nameFa;

      label = source;
      color = 'warning';
      icon = <EqualizerIcon sx={{ fontSize: 16 }} />;
    }

    return (
      <Chip
        icon={icon}
        label={
          <Stack direction="column" spacing={0.25} alignItems="flex-start">
            {strategy === 'AUTOMATIC_ESTJT' && (
              <span
                style={{ fontSize: '0.65rem', opacity: 0.85, fontWeight: 500 }}
              >
                مرجع قیمتی
              </span>
            )}
            <span>{label}</span>
          </Stack>
        }
        size="small"
        sx={{
          fontWeight: 600,
          fontSize: '0.75rem',
          height: 'auto', // ارتفاع خودکار

          borderRadius: '8px',

          // اجازه شکستن خط به لیبل داخلی Chip
          '& .MuiChip-label': {
            display: 'block',
            whiteSpace: 'normal',
            px: 1,
          },

          // هماهنگی آیکون هنگام ۲ خطه شدن
          '& .MuiChip-icon': {
            alignSelf: 'center',
            m: 0,
            ml: 0.75,
          },

          // رنگ‌بندی حرفه‌ای و شیک
          bgcolor: 'transparent',
          color:
            strategy === 'AUTOMATIC_COMPETITOR'
              ? '#6d28d9'
              : strategy === 'AUTOMATIC_ESTJT'
                ? '#1d4ed8' // رنگ آبی متین بجای نارنجی هشدار
                : '#475569',

          border: 'none',
          // borderColor:
          //   strategy === 'AUTOMATIC_COMPETITOR'
          //     ? '#ddd6fe'
          //     : strategy === 'AUTOMATIC_ESTJT'
          //       ? '#bfdbfe'
          //       : '#e2e8f0',
        }}
      />
    );
  };

  // --- TABLE HEAD ---
  const renderTableHead = () => (
    <TableHead>
      <TableRow
        sx={{
          background: buybackTheme.headerGradient,
          '& th': {
            color: '#fff',
            fontWeight: 700,
            py: 2,
            px: { xs: 1, md: 2 },
            fontSize: '0.85rem',
            borderBottom: 'none',
            whiteSpace: 'nowrap',
            letterSpacing: '0.5px',
            textShadow: '0 1px 2px rgba(0,0,0,0.1)',
            '&:first-of-type': {
              borderTopLeftRadius: '16px',
            },
            '&:last-child': {
              borderTopRightRadius: '16px',
            },
          },
        }}
      >
        <TableCell>شمش</TableCell>
        <TableCell>قیمت خرید</TableCell>
        <TableCell>تفاوت با مرجع قیمت</TableCell>
        <TableCell>استراتژی</TableCell>
        <TableCell>هر گرم (دستی)</TableCell>
        <TableCell>{'اختلاف'}</TableCell>
        <TableCell>ارقام گرد‌شده</TableCell>
        <TableCell>توسط</TableCell>
      </TableRow>
    </TableHead>
  );

  // --- SKELETON RENDERER ---
  const renderSkeletonRows = () =>
    [...Array(6)].map((_, index) => (
      <TableRow key={index}>
        {[...Array(8)].map((_, i) => (
          <TableCell key={i} sx={{ py: 2 }}>
            <Skeleton
              variant="rounded"
              width={i === 0 ? 80 : 50}
              height={24}
              sx={{ borderRadius: 1 }}
            />
          </TableCell>
        ))}
      </TableRow>
    ));

  // --- ROW RENDERER ---
  const renderTableRow = (bullion, index) => {
    const profitMargin = Number(bullion.buybackProfitMargin);
    const profitPercentage = Number(bullion.buybackProfitPercentage);

    const profitIcon =
      profitPercentage > 0 ? (
        <TrendingUpRounded fontSize="small" />
      ) : profitPercentage < 0 ? (
        <TrendingDownRounded fontSize="small" />
      ) : null;

    // Standard financial colors for profit/loss (regardless of theme)
    const profitColor =
      profitPercentage > 0
        ? '#10b981'
        : profitPercentage < 0
          ? '#ef4444'
          : '#64748b';

    return (
      <TableRow
        key={bullion.key}
        onClick={() =>
          openDialog(
            `${bullion.name}`,
            <BuybackPriceDialog
              strategyDefault={bullion.buybackStrategy}
              transactionType="BUY"
              is995={!!bullion.key.includes('GB_995')}
              manualBaseBuybackPriceDefault={bullion.manualBaseBuybackPrice}
              priceDefault={bullion.buybackPrice}
              bullionWeight={bullion.weight}
              trig={trig}
              trigger={trigger}
              strategy={bullion.buybackStrategy}
              cutNumberDefault={bullion.buybackCutNumber}
              profitPercentageDefault={
                getprofitPercentage(
                  bullionsConfig?.find(b => b.productType === bullion.key)
                ).profitPercentage
              }
              bullionData={bullionsConfig?.find(
                b => b.productType === bullion.key
              )}
              manualPriceLessThanThreshhold={manualPriceLessThanThreshhold}
              manualPriceMoreThanThreshhold={manualPriceMoreThanThreshhold}
              setBullionsConfig={setBullionsConfig}
              onClick={bullion.dialogProps.onClick}
            />
          )
        }
        sx={{
          cursor: 'pointer',
          transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          borderBottom: '1px solid #fff7ed', // Light orange border
          '&:last-child td': { borderBottom: 0 },
          '&:hover': {
            backgroundColor: buybackTheme.hover,
            transform: 'scale(1.002) translateX(-4px)',
            boxShadow: '0 4px 12px rgba(234, 88, 12, 0.15)', // Orange glow
            zIndex: 1,
            position: 'relative',
          },
          '& td': {
            color: '#334155',
            fontSize: '0.85rem',
            py: 2.2,
            px: { xs: 1, md: 2 },
            fontWeight: 500,
            whiteSpace: 'nowrap',
          },
        }}
      >
        <TableCell sx={{ fontWeight: 700, color: buybackTheme.dark }}>
          {bullion.name}
        </TableCell>

        <TableCell>
          <Tooltip
            title={`هرگرم ${(
              Number(bullion.buybackPrice) / bullion.weight
            ).toLocaleString('fa-IR')} ریال`}
            arrow
            placement="top"
          >
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
              <Typography
                component="span"
                sx={{
                  fontWeight: 800,
                  fontSize: '1rem',
                  color: buybackTheme.dark, // Orange Price
                }}
              >
                {Number(bullion.buybackPrice).toLocaleString('fa-IR')}
              </Typography>
              <Typography
                component="span"
                variant="caption"
                color="text.secondary"
              >
                ریال
              </Typography>
            </Box>
          </Tooltip>
        </TableCell>

        <TableCell>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              color:
                profitMargin > 0
                  ? '#16a34a'
                  : profitMargin < 0
                    ? '#dc2626'
                    : '#334155',
              bgcolor:
                profitMargin > 0
                  ? '#dcfce7'
                  : profitMargin < 0
                    ? '#fee2e2'
                    : '#f1f5f9',
              px: 1,
              py: 0.5,
              borderRadius: '6px',
            }}
          >
            <Typography variant="body2" fontWeight={700}>
              {profitMargin.toLocaleString('fa-IR')}%
            </Typography>
          </Box>
        </TableCell>

        <TableCell>{renderStrategyChip(bullion.buybackStrategy)}</TableCell>

        <TableCell sx={{ fontFamily: 'monospace' }}>
          {bullion.buybackStrategy === 'MANUAL' ? (
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
              <Typography
                component="span"
                sx={{
                  fontWeight: 600,
                  fontSize: '1rem',
                  color: buybackTheme.dark,
                }}
              >
                {Number(bullion.manualBaseBuybackPrice).toLocaleString('fa-IR')}
              </Typography>
              <Typography
                component="span"
                variant="caption"
                color="text.secondary"
              >
                ریال
              </Typography>
            </Box>
          ) : (
            '---'
          )}
        </TableCell>

        <TableCell>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              color: profitColor,
            }}
          >
            {profitIcon}
            <Typography variant="body2" fontWeight={700}>
              {bullion.buybackProfitPercentage !== null
                ? `${Number(bullion.buybackProfitPercentage ?? 0).toLocaleString('fa-IR')}%`
                : '---'}
            </Typography>
          </Box>
        </TableCell>

        <TableCell>
          {bullion.buybackCutNumber !== null ? (
            <Chip
              label={`${Number(bullion.buybackCutNumber).toLocaleString('fa-IR')} رقم`}
              size="small"
              // Light Orange Chip
              sx={{
                bgcolor: 'transparent',
                border: 'none',
                color: '#c2410c',
                fontWeight: 600,
                height: 22,
              }}
            />
          ) : (
            '---'
          )}
        </TableCell>

        <TableCell
          sx={{
            display: 'flex',
            flexDirection: 'column',

            fontWeight: 700,
            color: buybackTheme.dark,
          }}
        >
          <Chip
            label={new Date(bullion.buybackCreatedAt).toLocaleString('fa-IR')}
            size="small"
            sx={{
              bgcolor: 'transparent',
              border: 'none',
              fontWeight: 600,
              height: 20,
            }}
          />
          <Chip
            label={bullion.buybackCreatedBy}
            size="small"
            sx={{
              bgcolor: 'transparent',
              border: 'none',
              fontWeight: 600,
              height: 20,
            }}
          />
        </TableCell>
      </TableRow>
    );
  };

  const renderDividerRow = () => (
    <TableRow>
      <TableCell colSpan={8} sx={{ p: 0, height: '16px', border: 'none' }} />
    </TableRow>
  );

  return (
    <Grid item xs={12}>
      <Fade in={true} timeout={600}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: '24px',
            border: `1px solid ${buybackTheme.border}`,
            backgroundColor: buybackTheme.bg,
            boxShadow: buybackTheme.shadow,
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          {/* --- Header Section --- */}
          <Box
            sx={{
              p: { xs: 2, md: 3 },
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              borderBottom: '1px solid #fff7ed',
            }}
          >
            <Avatar
              variant="rounded"
              sx={{
                bgcolor: buybackTheme.light,
                color: buybackTheme.primary,
                width: 48,
                height: 48,
                borderRadius: '14px',
              }}
            >
              <LocalMallTwoTone fontSize="medium" />
            </Avatar>
            <Box>
              <Typography
                variant="h6"
                fontWeight={800}
                color="#7c2d12" // Deep Brown/Orange
                letterSpacing="-0.5px"
              >
                لیست قیمت خرید
              </Typography>
              {/* <Stack direction="row" alignItems="center" gap={1}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: '#22c55e',
                    boxShadow: '0 0 8px #22c55e',
                  }}
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight={500}
                >
                  بروزرسانی لحظه‌ای فعال است
                </Typography>
              </Stack> */}
            </Box>
          </Box>

          {/* --- Data Table --- */}
          <TableContainer sx={{ px: 1, pb: 1 }}>
            <Table>
              {!isPriceFirstLoading && renderTableHead()}
              <TableBody>
                {isPriceFirstLoading ? (
                  renderSkeletonRows()
                ) : (
                  <>
                    {firstCategory.map((bullion, index) =>
                      renderTableRow(bullion, index)
                    )}
                    {renderDividerRow()}
                    {secondCategory.map((bullion, index) =>
                      renderTableRow(bullion, index + 7)
                    )}
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Fade>
    </Grid>
  );
};

export default CurrentBuybackPrice;
